/* Product page behaviour: the photo slider, the quantity stepper and the variant
   picker. Written against the markup in sections/product.liquid.

   Swiper is loaded from the CDN with `async` in layout/theme.liquid, so anything that
   touches it waits for `load` the way the other sliders in this theme do. Everything
   else binds straight away — a shopper can reach the stepper long before the images
   have finished downloading. */
(function () {
  'use strict';

  var root = document.querySelector('[data-product-root]');
  if (!root) return;

  var strings = window.productStrings || {};

  /* ---------------------------------------------------------------- quantity */

  function bindQuantity() {
    root.querySelectorAll('[data-quantity-selector]').forEach(function (selector) {
      var input = selector.querySelector('[data-quantity-input]');
      if (!input) return;

      selector.querySelectorAll('[data-quantity-step]').forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(button.dataset.quantityStep, 10) || 0;
          var min = parseInt(input.min, 10);
          var max = parseInt(input.max, 10);
          var next = (parseInt(input.value, 10) || 0) + step;

          if (!isNaN(min)) next = Math.max(min, next);
          if (!isNaN(max)) next = Math.min(max, next);

          input.value = next;
          /* Dispatched so anything else listening on the field — a cart drawer, a
             price-per-unit readout — sees the change a click makes, which setting
             .value on its own does not fire. */
          input.dispatchEvent(new Event('change', { bubbles: true }));
          syncQuantity(selector, input);
        });
      });

      /* Typing straight into the field can land under the minimum or on nothing at
         all; the form would then post an empty quantity. */
      input.addEventListener('change', function () {
        var min = parseInt(input.min, 10);
        var value = parseInt(input.value, 10);
        if (isNaN(value) || (!isNaN(min) && value < min)) {
          input.value = isNaN(min) ? 1 : min;
        }
        syncQuantity(selector, input);
      });

      syncQuantity(selector, input);
    });
  }

  function syncQuantity(selector, input) {
    var min = parseInt(input.min, 10);
    var max = parseInt(input.max, 10);
    var value = parseInt(input.value, 10) || 0;

    var minus = selector.querySelector('[data-quantity-step="-1"]');
    var plus = selector.querySelector('[data-quantity-step="1"]');

    if (minus) minus.disabled = !isNaN(min) && value <= min;
    if (plus) plus.disabled = !isNaN(max) && value >= max;
  }

  /* ----------------------------------------------------------------- variants */

  var variantData = readVariants();
  var stage = null;

  function readVariants() {
    var node = root.querySelector('[data-product-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (error) {
      return [];
    }
  }

  function selectedOptions() {
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-product-option]'));
    return groups
      .sort(function (a, b) {
        return Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition);
      })
      .map(function (group) {
        var chosen = group.querySelector('.dropdown__option[aria-selected="true"]');
        return chosen ? chosen.getAttribute('value') : null;
      });
  }

  function findVariant(options) {
    for (var i = 0; i < variantData.length; i++) {
      var variant = variantData[i];
      var match = true;
      for (var j = 0; j < options.length; j++) {
        if (variant.options[j] !== options[j]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  }

  /* The disabled look lives on the button snippet's wrapper as well as on the button,
     so both have to move together or a re-enabled button keeps the dimmed wrapper. */
  function setButtonState(button, enabled, label) {
    if (!button) return;
    button.disabled = !enabled;
    if (label) button.textContent = label;

    var wrapper = button.closest('.btn');
    if (wrapper) wrapper.classList.toggle('btn--disabled', !enabled);
  }

  function setPurchaseState(enabled, label) {
    setButtonState(root.querySelector('[data-product-add]'), enabled, label);
    setButtonState(root.querySelector('.product__buy-now button'), enabled, null);
  }

  function applyVariant(variant) {
    var idField = root.querySelector('[data-product-variant-id]');
    /* Both prices carry heading--title-6 — the heading snippet takes no class of its
       own — so the compare-at is excluded by name rather than by document order. */
    var priceNode = root.querySelector(
      '.product__price-row .heading--title-6:not(.product__compare-price)'
    );
    var compareNode = root.querySelector('.product__compare-price');
    var instalments = root.querySelector('[data-product-installments]');

    if (!variant) {
      /* A combination the shop does not stock. The form is left pointing at the last
         real variant and the button is locked, rather than posting an id that would
         fail at the cart. */
      setPurchaseState(false, strings.unavailable || 'Unavailable');
      return;
    }

    if (idField) idField.value = variant.id;

    if (priceNode) priceNode.textContent = variant.price_formatted;

    if (compareNode) {
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareNode.hidden = !onSale;
      if (onSale) compareNode.textContent = variant.compare_at_price_formatted;
    }

    if (instalments && strings.installmentsTemplate) {
      instalments.textContent = strings.installmentsTemplate.replace(
        '[amount]',
        variant.instalment_formatted
      );
    }

    setPurchaseState(
      variant.available,
      variant.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out'
    );

    /* Figma gives the variant no image treatment of its own, so the gallery just
       moves to the photo the variant is attached to when it has one. */
    if (stage && variant.featured_image_position) {
      stage.slideTo(variant.featured_image_position - 1);
    }

    var url = root.dataset.productUrl;
    if (url && window.history && window.history.replaceState) {
      window.history.replaceState({}, '', url + '?variant=' + variant.id);
    }
  }

  /* The dropdown snippet ships no behaviour of its own — shop-all.js drives its copy
     the same way. Kept local so the two never fight over one another's markup. */
  function bindOptionDropdowns() {
    root.querySelectorAll('[data-product-option] .dropdown').forEach(function (dropdown) {
      var trigger = dropdown.querySelector('.dropdown__trigger');
      var menu = dropdown.querySelector('.dropdown__menu');
      var label = dropdown.querySelector('[data-dropdown-label]');
      if (!trigger || !menu) return;

      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        menu.hidden = open;
      });

      menu.addEventListener('click', function (event) {
        var option = event.target.closest('.dropdown__option');
        if (!option) return;

        menu.querySelectorAll('.dropdown__option').forEach(function (node) {
          node.setAttribute('aria-selected', String(node === option));
        });
        if (label) label.textContent = option.textContent.trim();

        trigger.setAttribute('aria-expanded', 'false');
        menu.hidden = true;

        applyVariant(findVariant(selectedOptions()));
      });

      document.addEventListener('click', function (event) {
        if (!dropdown.contains(event.target)) {
          trigger.setAttribute('aria-expanded', 'false');
          menu.hidden = true;
        }
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          trigger.setAttribute('aria-expanded', 'false');
          menu.hidden = true;
        }
      });
    });
  }

  /* ------------------------------------------------------------------ gallery */

  function buildSliders() {
    if (typeof window.Swiper !== 'function') return;

    var stageEl = root.querySelector('[data-product-stage]');
    if (!stageEl) return;

    var thumbsEl = root.querySelector('[data-product-thumbs]');
    var thumbs = null;

    if (thumbsEl) {
      thumbs = new window.Swiper(thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: window.matchMedia('(max-width: 767px)').matches ? 8 : 10,
        freeMode: true,
        watchSlidesProgress: true
      });
    }

    /* No arrows and no dots by design: the thumbnails are the only control, and on
       touch the slide itself is the gesture. */
    stage = new window.Swiper(stageEl, {
      slidesPerView: 1,
      navigation: false,
      pagination: false,
      thumbs: thumbs ? { swiper: thumbs } : undefined
    });
  }

  bindQuantity();
  bindOptionDropdowns();

  if (document.readyState === 'complete') {
    buildSliders();
  } else {
    window.addEventListener('load', buildSliders);
  }
})();
